import React, { useCallback, useState } from 'react';

import { DataLake } from '@graylog/enterprise-api';

import { Modal } from 'components/bootstrap';
import { Pluralize, ModalSubmit } from 'components/common';
import RetrievalHelp from 'data-lake/RetrievalHelp';
import useSelectedEntities from 'components/common/EntityDataTable/hooks/useSelectedEntities';
import UserNotification from 'util/UserNotification';
import useSelectableLogViewMessages from 'logview/components/contexts/useSelectableLogViewMessages';
import useSelectedDataLake from 'data-lake/hooks/useSelectedDataLake';
import useRetrieval from 'data-lake/preview/hooks/useRetrieval';

type Props<BulkRetrievalPayload> = {
  onCancel: () => void;
  retrievalPayload: BulkRetrievalPayload;
};

const BulkRetrievalModal = <BulkRetrievalPayload extends unknown>({
  onCancel,
  retrievalPayload,
}: Props<BulkRetrievalPayload>) => {
  const { selectedEntities } = useSelectedEntities();
  const { selectableLogViewMessages } = useSelectableLogViewMessages();
  const { selectedDataLake } = useSelectedDataLake();
  const { bulkRetrievePayload } = useRetrieval();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = useCallback(() => {
    const selectedSet = new Set(selectedEntities);
    const selectedMessages = selectableLogViewMessages.filter(({ id }) => selectedSet.has(id));

    setIsSubmitting(true);

    DataLake.retrieveMultipleMessages(bulkRetrievePayload(selectedMessages, selectedDataLake.id, retrievalPayload))
      .then(() => {
        UserNotification.success('Data Lake messages retrieval operation launched successfully.', 'Success');
        onCancel();
      })
      .catch((error) => {
        UserNotification.error(`An error occurred while retrieving data. ${error}`);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [
    selectedEntities,
    selectableLogViewMessages,
    bulkRetrievePayload,
    selectedDataLake.id,
    retrievalPayload,
    onCancel,
  ]);

  return (
    <Modal show onHide={onCancel}>
      <Modal.Header>
        <Modal.Title>Retrieving from Data Lake</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          You are about to retrieve {selectedEntities?.length}{' '}
          <Pluralize singular="log" plural="logs" value={selectedEntities?.length} />.
        </p>
        <RetrievalHelp />
      </Modal.Body>
      <Modal.Footer>
        <ModalSubmit
          onCancel={onCancel}
          submitButtonText="Retrieve"
          isSubmitting={isSubmitting}
          submitLoadingText="Retrieving..."
          onSubmit={onSubmit}
          isAsyncSubmit
        />
      </Modal.Footer>
    </Modal>
  );
};

export default BulkRetrievalModal;
